import React, { useEffect, useState } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Loader2, TrendingUp, Users, ShoppingCart, BarChart3, Target,
  Download, RefreshCw, AlertTriangle, ArrowUpRight, Lightbulb,
  PieChart, Store, Briefcase, ChevronRight, Brain
} from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#ec4899', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#84cc16'];

function MetricCard({ icon: Icon, label, value, sub, color = 'pink' }) {
  const colorMap = { pink: 'bg-pink-50 text-pink-600', blue: 'bg-blue-50 text-blue-600', emerald: 'bg-emerald-50 text-emerald-600', amber: 'bg-amber-50 text-amber-600', violet: 'bg-violet-50 text-violet-600' };
  return (
    <Card className="border-gray-200" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-9 h-9 rounded-lg ${colorMap[color] || colorMap.pink} flex items-center justify-center`}>
            <Icon className="w-4.5 h-4.5" />
          </div>
          <p className="text-[10px] uppercase font-semibold tracking-wider text-gray-400">{label}</p>
        </div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function BarSegment({ items, maxValue, labelKey = 'category', valueKey = 'count', colorIdx = 0 }) {
  return (
    <div className="space-y-2">
      {items.map((item, i) => {
        const val = item[valueKey] || 0;
        const pct = maxValue > 0 ? (val / maxValue) * 100 : 0;
        return (
          <div key={item[labelKey] || i} className="flex items-center gap-3">
            <span className="text-xs text-gray-600 w-28 truncate capitalize">{item[labelKey]?.replace(/_/g, ' ') || 'N/A'}</span>
            <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: COLORS[(i + colorIdx) % COLORS.length] }} />
            </div>
            <span className="text-xs font-semibold text-gray-700 w-16 text-right">{typeof val === 'number' && val > 999 ? `${(val/1000).toFixed(1)}K` : val}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function UserAnalytics() {
  const [overview, setOverview] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [o, p] = await Promise.all([
        superAdminAPI.getAnalyticsOverview(30),
        superAdminAPI.getBuyingPatterns(),
      ]);
      setOverview(o);
      setPatterns(p);
    } catch (err) {
      toast.error('Failed to load analytics');
      console.error(err);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleExportTraining = async () => {
    setExporting(true);
    try {
      const data = await superAdminAPI.getTrainingData(90);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pebbles_training_data_${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.data_points} data points`);
    } catch (err) { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  const s = overview?.summary || {};
  const tabs = [
    { id: 'overview', label: 'Overview', icon: PieChart },
    { id: 'buying', label: 'Buying Patterns', icon: ShoppingCart },
    { id: 'vendors', label: 'Vendor Intelligence', icon: Store },
    { id: 'training', label: 'AI Training', icon: Brain },
  ];

  return (
    <div className="space-y-6" data-testid="user-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">User Intelligence</h1>
          <p className="text-sm text-gray-500">Buying behavior, patterns & decision data</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} className="h-8 text-xs" data-testid="refresh-analytics">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportTraining} disabled={exporting} className="h-8 text-xs" data-testid="export-training-btn">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            Export AI Data
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              activeTab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`} data-testid={`tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <MetricCard icon={Users} label="Active Employees" value={s.total_active_employees} color="blue" />
            <MetricCard icon={Target} label="BVN Adoption" value={`${s.bvn_adoption_pct}%`} sub={`${s.employees_with_bvn} verified`} color="emerald" />
            <MetricCard icon={ShoppingCart} label="Benefit Elections" value={s.total_benefit_elections} color="pink" />
            <MetricCard icon={Store} label="Active Vendors" value={s.total_active_vendors} color="violet" />
            <MetricCard icon={BarChart3} label="Events Tracked" value={s.tracked_events} color="amber" />
          </div>

          {/* Benefit Category Breakdown */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-pink-500" /> Benefit Categories (by elections)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {overview?.benefit_categories?.length ? (
                  <BarSegment items={overview.benefit_categories} maxValue={Math.max(...overview.benefit_categories.map(c => c.elections))} valueKey="elections" />
                ) : <p className="text-xs text-gray-400 text-center py-6">No benefit elections yet</p>}
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-500" /> Feature Usage (last 30d)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {overview?.feature_usage?.length ? (
                  <BarSegment items={overview.feature_usage.slice(0, 8)} maxValue={Math.max(...overview.feature_usage.map(f => f.count))} labelKey="action" valueKey="count" colorIdx={3} />
                ) : <p className="text-xs text-gray-400 text-center py-6">No feature usage data yet</p>}
              </CardContent>
            </Card>
          </div>

          {/* Company Engagement */}
          <Card className="border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-violet-500" /> Company Engagement
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-[11px]">
                <thead className="bg-gray-50"><tr>
                  <th className="text-left py-2 px-4 font-semibold text-gray-500">Company</th>
                  <th className="text-right py-2 px-4 font-semibold text-gray-500">Employees</th>
                  <th className="text-right py-2 px-4 font-semibold text-gray-500">Elections</th>
                  <th className="text-right py-2 px-4 font-semibold text-gray-500">Adoption</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {overview?.company_engagement?.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50/50">
                      <td className="py-2 px-4 font-medium text-gray-900">{c.name}</td>
                      <td className="py-2 px-4 text-right text-gray-600">{c.employees}</td>
                      <td className="py-2 px-4 text-right text-gray-600">{c.elections}</td>
                      <td className="py-2 px-4 text-right">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                          c.adoption_rate > 50 ? 'bg-emerald-50 text-emerald-600' : c.adoption_rate > 20 ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'
                        }`}>{c.adoption_rate}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!overview?.company_engagement?.length && <p className="text-xs text-gray-400 text-center py-6">No company data yet</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Buying Patterns Tab */}
      {activeTab === 'buying' && patterns && (
        <div className="space-y-6">
          {/* Market Gaps */}
          <Card className="border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Target className="w-4 h-4 text-pink-500" /> Market Gaps — Supply vs Demand
              </CardTitle>
              <p className="text-[10px] text-gray-400 mt-0.5">High demand-per-vendor ratio = opportunity to onboard more vendors</p>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-[11px]">
                <thead className="bg-gray-50"><tr>
                  <th className="text-left py-2 px-4 font-semibold text-gray-500">Category</th>
                  <th className="text-right py-2 px-4 font-semibold text-gray-500">Demand</th>
                  <th className="text-right py-2 px-4 font-semibold text-gray-500">Budget Pool</th>
                  <th className="text-right py-2 px-4 font-semibold text-gray-500">Vendors</th>
                  <th className="text-right py-2 px-4 font-semibold text-gray-500">Products</th>
                  <th className="text-right py-2 px-4 font-semibold text-gray-500">Gap Signal</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {patterns.market_gaps?.map(g => (
                    <tr key={g.category} className="hover:bg-gray-50/50">
                      <td className="py-2 px-4 font-medium text-gray-900 capitalize">{g.category?.replace(/_/g, ' ')}</td>
                      <td className="py-2 px-4 text-right text-gray-600">{g.demand}</td>
                      <td className="py-2 px-4 text-right text-gray-600">NGN {Number(g.budget).toLocaleString()}</td>
                      <td className="py-2 px-4 text-right text-gray-600">{g.vendors_available}</td>
                      <td className="py-2 px-4 text-right text-gray-600">{g.products_available}</td>
                      <td className="py-2 px-4 text-right">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                          g.gap_signal === 'high' ? 'bg-red-50 text-red-600' : g.gap_signal === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'
                        }`}>{g.gap_signal}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!patterns.market_gaps?.length && <p className="text-xs text-gray-400 text-center py-6">No pattern data yet — elections will populate this</p>}
            </CardContent>
          </Card>

          {/* Salary Distribution & Employment Mix */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-emerald-500" /> Buying Power (Salary Bands)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {patterns.salary_distribution?.length ? (
                  <BarSegment items={patterns.salary_distribution} maxValue={Math.max(...patterns.salary_distribution.map(s => s.count))} labelKey="band" valueKey="count" colorIdx={2} />
                ) : <p className="text-xs text-gray-400 text-center py-6">No salary data</p>}
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" /> Employment Type Mix
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {patterns.employment_mix?.length ? (
                  <BarSegment items={patterns.employment_mix} maxValue={Math.max(...patterns.employment_mix.map(e => e.count))} labelKey="type" valueKey="count" colorIdx={5} />
                ) : <p className="text-xs text-gray-400 text-center py-6">No data</p>}
              </CardContent>
            </Card>
          </div>

          {/* Recommendations */}
          {patterns.recommendations?.length > 0 && (
            <Card className="border-pink-200 bg-pink-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-pink-500" /> Actionable Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {patterns.recommendations.map((r, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100" data-testid={`rec-${i}`}>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase shrink-0 mt-0.5 ${
                      r.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                    }`}>{r.priority}</span>
                    <div>
                      <p className="text-xs font-semibold text-gray-900">{r.title}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">{r.detail}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Vendor Intelligence Tab */}
      {activeTab === 'vendors' && (
        <div className="space-y-6">
          <Card className="border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Store className="w-4 h-4 text-violet-500" /> Top Vendors by Employee Elections
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-[11px]">
                <thead className="bg-gray-50"><tr>
                  <th className="text-left py-2 px-4 font-semibold text-gray-500">#</th>
                  <th className="text-left py-2 px-4 font-semibold text-gray-500">Vendor</th>
                  <th className="text-left py-2 px-4 font-semibold text-gray-500">Category</th>
                  <th className="text-right py-2 px-4 font-semibold text-gray-500">Elections</th>
                  <th className="text-right py-2 px-4 font-semibold text-gray-500">Total Allocated</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {overview?.top_vendors?.map((v, i) => (
                    <tr key={v.id} className="hover:bg-gray-50/50">
                      <td className="py-2 px-4 text-gray-400">{i + 1}</td>
                      <td className="py-2 px-4 font-medium text-gray-900">{v.name}</td>
                      <td className="py-2 px-4 text-gray-500 capitalize">{v.category?.replace(/_/g, ' ')}</td>
                      <td className="py-2 px-4 text-right text-gray-600">{v.elections}</td>
                      <td className="py-2 px-4 text-right text-gray-600">NGN {Number(v.total_allocated).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!overview?.top_vendors?.length && <p className="text-xs text-gray-400 text-center py-6">No vendor data yet</p>}
            </CardContent>
          </Card>

          {/* Flex Spending */}
          <Card className="border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-emerald-500" /> Flex Benefit Spending
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {overview?.flex_spending?.length ? (
                <BarSegment items={overview.flex_spending} maxValue={Math.max(...overview.flex_spending.map(f => f.total_spent))} labelKey="category" valueKey="total_spent" colorIdx={1} />
              ) : <p className="text-xs text-gray-400 text-center py-6">No flex spending data yet</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Training Tab */}
      {activeTab === 'training' && (
        <div className="space-y-6">
          <Card className="border-gray-200 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Brain className="w-6 h-6 text-pink-400" />
                </div>
                <div>
                  <h2 className="text-base font-bold">AI Training Data</h2>
                  <p className="text-xs text-gray-400">Anonymized behavioral patterns for training future AI agents</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-pink-400">{s.total_benefit_elections || 0}</p>
                  <p className="text-[10px] text-gray-400">Election Patterns</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-blue-400">{overview?.feature_usage?.length || 0}</p>
                  <p className="text-[10px] text-gray-400">Feature Signals</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-emerald-400">{overview?.flex_spending?.length || 0}</p>
                  <p className="text-[10px] text-gray-400">Spend Categories</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 mb-4">
                Export includes: benefit election patterns (category, amount, frequency by department and salary band), 
                flex spending patterns, feature usage frequency. All data is anonymized — no PII included.
              </p>
              <Button onClick={handleExportTraining} disabled={exporting}
                className="bg-pink-500 hover:bg-pink-600 text-white h-9 text-xs" data-testid="export-training-data">
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                Export Training Dataset (JSON)
              </Button>
            </CardContent>
          </Card>

          <Card className="border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-700">What this data trains</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {[
                { title: 'Benefit Recommendation Engine', desc: 'Predict which benefits an employee will choose based on department, salary band, and company patterns' },
                { title: 'Vendor Matching', desc: 'Auto-suggest vendors to onboard based on demand gaps and spending patterns' },
                { title: 'Budget Optimization', desc: 'Recommend optimal benefit allocations per employee segment' },
                { title: 'Churn Prediction', desc: 'Identify employees at risk based on declining platform engagement' },
                { title: 'Product Roadmap', desc: 'Prioritize features based on actual usage frequency and user behavior' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <ChevronRight className="w-4 h-4 text-pink-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-gray-900">{item.title}</p>
                    <p className="text-[11px] text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
